import React, { useState } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowDownCircle, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function ManualInflowPage() {
  const [form, setForm] = useState({ virtual_account_number: '', amount: '', reference: '', sender_name: '', narration: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.virtual_account_number || !form.amount || !form.reference) {
      toast.error('Virtual account number, amount, and reference are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await superAdminAPI.processManualInflow({
        ...form,
        amount: parseFloat(form.amount),
      });
      setResult(data);
      toast.success(`NGN ${parseFloat(form.amount).toLocaleString()} credited to ${data.company}`);
      setForm({ virtual_account_number: '', amount: '', reference: '', sender_name: '', narration: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to process manual inflow';
      toast.error(detail);
      setResult({ status: 'error', message: detail });
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="manual-inflow-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Manual Inflow Processing</h1>
        <p className="text-sm text-gray-500 mt-1">Credit a company wallet for missed webhooks or manual reconciliation</p>
      </div>

      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Use with caution</p>
            <p className="mt-0.5 text-amber-700">This directly credits a company wallet. Verify the payment was actually received at Assets MFB before processing. All actions are audit-logged.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Virtual Account Number <span className="text-red-500">*</span></label>
              <input type="text" value={form.virtual_account_number} onChange={e => update('virtual_account_number', e.target.value)}
                placeholder="e.g. 8133278101" required
                className="w-full px-3 py-2.5 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
                data-testid="inflow-va-number" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Amount (NGN) <span className="text-red-500">*</span></label>
              <input type="number" step="0.01" min="0.01" value={form.amount} onChange={e => update('amount', e.target.value)}
                placeholder="e.g. 87.00" required
                className="w-full px-3 py-2.5 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
                data-testid="inflow-amount" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Reference ID <span className="text-red-500">*</span></label>
              <input type="text" value={form.reference} onChange={e => update('reference', e.target.value)}
                placeholder="e.g. 69AB089EA4DA7E25C31E8FF3" required
                className="w-full px-3 py-2.5 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
                data-testid="inflow-reference" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Sender Name</label>
              <input type="text" value={form.sender_name} onChange={e => update('sender_name', e.target.value)}
                placeholder="e.g. John Doe"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
                data-testid="inflow-sender-name" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Narration / Description</label>
              <input type="text" value={form.narration} onChange={e => update('narration', e.target.value)}
                placeholder="e.g. Payment for services"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
                data-testid="inflow-narration" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white h-11"
              data-testid="inflow-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowDownCircle className="w-4 h-4 mr-2" />}
              {loading ? 'Processing...' : 'Process Manual Inflow'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {result && result.status === 'success' && (
        <Card className="border-emerald-200 bg-emerald-50/30" data-testid="inflow-result">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-sm font-bold text-emerald-700">Inflow Processed Successfully</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['Company', result.company],
                ['Virtual Account', result.virtual_account],
                ['Amount', `NGN ${result.amount?.toLocaleString()}`],
                ['Reference', result.reference],
                ['Balance Before', `NGN ${result.balance_before?.toLocaleString()}`],
                ['Balance After', `NGN ${result.balance_after?.toLocaleString()}`],
                ['Transaction ID', result.transaction_id],
                ['Processed By', result.processed_by],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="font-mono text-gray-900 break-all">{val}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {result && result.status === 'error' && (
        <Card className="border-red-200 bg-red-50/30" data-testid="inflow-error">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-700">Processing Failed</p>
              <p className="text-sm text-red-600 mt-1">{result.message}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
